import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { paths } from '@constants/paths';
import { Sim } from '@generated/gql/graphql';

import { useRemoveMobileService } from './useRemoveMobileService';
import { useRemoveMobileQuote } from './useRemoveSim';

const useRemoveDialog = (
  selectedSims: Sim[],
  setHasFormChanged: (hasFormChanged: boolean) => void,
  clearSims: () => void
) => {
  const navigate = useNavigate();
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [removedSim, setRemovedSim] = useState<Nullable<Sim>>(null);
  const { removeSim } = useRemoveMobileQuote();
  const { removeService, isLoading: isRemovingService } =
    useRemoveMobileService();

  const reset = () => {
    setIsRemoveModalOpen(false);
    setRemovedSim(null);
  };

  const removeSingleSim = () => {
    if (!removedSim) {
      // Should never happen
      throw new Error('useRemoveSimDialog: No quote selected for removal');
    }
    reset();
    removeSim(removedSim.id);
    setHasFormChanged(true);
  };

  const handleRemoveSimClick = (simId: string) => {
    const removedSim = selectedSims.find(({ id }) => id === simId);

    // Should never happen
    if (!removedSim) {
      throw new Error(
        `Data inconsistency. useRemoveSimDialog: No quote found with id ${simId}`
      );
    }

    setIsRemoveModalOpen(true);
    setRemovedSim(removedSim);
  };

  const removeMobileService = () => {
    reset();
    removeService(selectedSims.length, () => {
      clearSims();
      navigate(paths.bundle);
    });
  };

  const handleRemove = () => {
    if (removedSim) {
      removeSingleSim();
    } else {
      removeMobileService();
    }
  };

  return {
    isRemoveModalOpen,
    isRemoving: isRemovingService,
    removeSimName: removedSim?.selectedTariff.name || '',
    handleModalClose: reset,
    handleRemoveSimClick,
    handleRemove,
    handleMobileServiceRemoved: () => setIsRemoveModalOpen(true),
  };
};

export { useRemoveDialog };
